import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import ProjectsSection from '../components/ProjectsSection';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Calendar, Pill, Users, Shield, Activity, Briefcase, Building, Phone, Mail } from 'lucide-react';

const API_URL = window.location.origin || '';

const Medical = () => {
  const { currentTeam } = useTeam();
  const [appointments, setAppointments] = useState([]);
  const [medications, setMedications] = useState([]);
  const [medicationLogs, setMedicationLogs] = useState([]);
  const [providers, setProviders] = useState([]);
  const [insurancePolicies, setInsurancePolicies] = useState([]);
  const [careLogs, setCareLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all medical data - only when currentTeam.id actually changes
  useEffect(() => {
    if (currentTeam?.id) {
      fetchMedicalData();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes, not the whole object

  const fetchMedicalData = async () => {
    // Only show loading spinner on initial load (when no data exists)
    if (medications.length === 0 && careLogs.length === 0) {
      setLoading(true);
    }
    try {
      await Promise.all([
        fetchAppointments(),
        fetchMedications(),
        fetchCareLogs()
      ]);
    } catch (error) {
      console.error('Error fetching medical data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/appointments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchMedications = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/medications`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMedications(data);
      }
    } catch (error) {
      console.error('Error fetching medications:', error);
    }
  };

  const fetchCareLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/care-logs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCareLogs(data);
      }
    } catch (error) {
      console.error('Error fetching care logs:', error);
    }
  };

  if (!currentTeam) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto fade-in">
          <h1 className="text-4xl font-bold mb-6 text-gray-900">Medical</h1>
          <Card className="p-8">
            <p className="text-gray-600">Please select a team to view medical information.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header with Quick Actions */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900">Medical</h1>
          <QuickActions position="top" />
        </div>

        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList>
            <TabsTrigger value="appointments">
              <Calendar className="w-4 h-4 mr-2" />
              Appointments
            </TabsTrigger>
            <TabsTrigger value="medications">
              <Pill className="w-4 h-4 mr-2" />
              Medications
            </TabsTrigger>
            <TabsTrigger value="providers">
              <Users className="w-4 h-4 mr-2" />
              Providers
            </TabsTrigger>
            <TabsTrigger value="insurance">
              <Shield className="w-4 h-4 mr-2" />
              Insurance
            </TabsTrigger>
            <TabsTrigger value="carelog">
              <Activity className="w-4 h-4 mr-2" />
              Care Log
            </TabsTrigger>
            <TabsTrigger value="projects">
              <Briefcase className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Appointments</h2>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Appointment
                </Button>
              </div>
              <div className="text-center py-12 text-gray-900">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="font-medium">No upcoming appointments.</p>
                <p className="text-sm mt-2 text-gray-600">Schedule medical appointments and track visits</p>
              </div>
            </Card>
          </TabsContent>

          {/* Medications Tab */}
          <TabsContent value="medications">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Medications</h2>
                <AddMedicationDialog onSuccess={fetchMedications} />
              </div>
              {medications.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Pill className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No medications added yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Track medications, dosages, and schedules</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {medications.map(med => (
                    <Card key={med.id} className="p-4 border-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-900">{med.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>Dosage:</strong> {med.dosage} | <strong>Frequency:</strong> {med.frequency}
                          </p>
                          {med.start_date && (
                            <p className="text-sm text-gray-600 mt-1">
                              <strong>Start:</strong> {new Date(med.start_date).toLocaleDateString()}
                              {med.end_date && ` - End: ${new Date(med.end_date).toLocaleDateString()}`}
                            </p>
                          )}
                          {med.side_effects && (
                            <p className="text-sm text-gray-600 mt-1">
                              <strong>Side Effects:</strong> {med.side_effects}
                            </p>
                          )}
                          {med.notes && (
                            <p className="text-sm text-gray-500 mt-1">{med.notes}</p>
                          )}
                        </div>
                        <LogMedicationDialog medicationId={med.id} medicationName={med.name} />
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Healthcare Providers</h2>
                <AddProviderDialog providers={providers} setProviders={setProviders} />
              </div>
              {providers.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No providers added yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Track doctors, specialists, and healthcare providers</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {providers.map(provider => (
                    <Card key={provider.id} className="p-4 border-2">
                      <h3 className="font-semibold text-lg text-gray-900">{provider.name}</h3>
                      <p className="text-sm text-gray-600 mb-3">{provider.specialty}</p>
                      <div className="space-y-2 text-sm text-gray-700">
                        {provider.practice && (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-gray-500" />
                            <span>{provider.practice}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gray-500" />
                          <span>{provider.phone}</span>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Insurance Tab */}
          <TabsContent value="insurance">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Insurance Policies</h2>
                <AddInsuranceDialog policies={insurancePolicies} setPolicies={setInsurancePolicies} />
              </div>
              {insurancePolicies.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No insurance policies added.</p>
                  <p className="text-sm mt-2 text-gray-600">Track health insurance, Medicare, and supplemental coverage</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {insurancePolicies.map(policy => (
                    <Card key={policy.id} className="p-4 border-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">{policy.provider}</h3>
                          <p className="text-sm text-gray-600">Policy #: {policy.policyNumber}</p>
                          <p className="text-sm text-gray-600">Type: {policy.type}</p>
                        </div>
                        <Badge className="bg-green-100 text-green-900 border-green-300">Active</Badge>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Care Log Tab */}
          <TabsContent value="carelog">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Daily Care Log</h2>
                <AddCareLogDialog onSuccess={fetchCareLogs} />
              </div>
              {careLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No care logs yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Track daily activities, vitals, mood, pain levels, and sleep</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {careLogs.map(log => (
                    <Card key={log.id} className="p-4 border-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-semibold text-lg text-gray-900">
                            {new Date(log.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                          
                          {log.meals && (
                            <div className="mt-2">
                              <strong className="text-gray-900">Meals:</strong>
                              <p className="text-sm text-gray-600">{log.meals}</p>
                            </div>
                          )}
                          
                          {log.activities && (
                            <div className="mt-2">
                              <strong className="text-gray-900">Activities:</strong>
                              <p className="text-sm text-gray-600">{log.activities}</p>
                            </div>
                          )}
                          
                          <div className="flex gap-4 mt-2 text-sm text-gray-700">
                            {log.mood_rating && <span>Mood: {log.mood_rating}/10</span>}
                            {log.sleep_quality && <span>Sleep: {log.sleep_quality}/10</span>}
                          </div>
                          
                          {log.vitals && Object.keys(log.vitals).length > 0 && (
                            <div className="mt-2 border-t pt-2">
                              <strong className="text-gray-900">Vitals:</strong>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600 mt-1">
                                {log.vitals.bp_systolic && log.vitals.bp_diastolic && (
                                  <span>BP: {log.vitals.bp_systolic}/{log.vitals.bp_diastolic}</span>
                                )}
                                {log.vitals.temp && <span>Temp: {log.vitals.temp}°F</span>}
                                {log.vitals.weight && <span>Weight: {log.vitals.weight} lbs</span>}
                                {log.vitals.heart_rate && <span>HR: {log.vitals.heart_rate} bpm</span>}
                                {log.vitals.o2_sat && <span>O2: {log.vitals.o2_sat}%</span>}
                                {log.vitals.pain && <span>Pain: {log.vitals.pain}/10</span>}
                              </div>
                            </div>
                          )}
                          
                          {log.bathroom_notes && (
                            <div className="mt-2">
                              <strong className="text-gray-900">Bathroom:</strong>
                              <p className="text-sm text-gray-600">{log.bathroom_notes}</p>
                            </div>
                          )}
                          
                          {log.mobility_notes && (
                            <div className="mt-2">
                              <strong className="text-gray-900">Mobility:</strong>
                              <p className="text-sm text-gray-600">{log.mobility_notes}</p>
                            </div>
                          )}
                          
                          {log.general_notes && (
                            <div className="mt-2">
                              <strong className="text-gray-900">Notes:</strong>
                              <p className="text-sm text-gray-600">{log.general_notes}</p>
                            </div>
                          )}
                        </div>
                        <span className="text-sm text-gray-500 whitespace-nowrap ml-2">
                          {log.date} {log.time}
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <ProjectsSection
              currentTeam={currentTeam}
              dashboardArea="medical"
              projectTypes={[
                'Find Specialist',
                'Get Second Opinion',
                'Insurance Appeal',
                'Research Treatment Options',
                'Coordinate Home Health',
                'Medical Equipment Setup',
                'Medication Review',
                'Medical Records Organization',
                'Other'
              ]}
            />
          </TabsContent>
        </Tabs>

        {/* Quick Actions - Bottom */}
        <div className="mt-6">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

// Add Medication Dialog
const AddMedicationDialog = ({ onSuccess }) => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: '',
    prescribing_provider_id: null,
    start_date: '',
    end_date: '',
    side_effects: '',
    notes: '',
    schedule_times: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/medications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Medication added successfully');
        setOpen(false);
        setFormData({
          name: '',
          dosage: '',
          frequency: '',
          prescribing_provider_id: null,
          start_date: '',
          end_date: '',
          side_effects: '',
          notes: '',
          schedule_times: []
        });
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add medication');
      }
    } catch (error) {
      console.error('Error adding medication:', error);
      toast.error('Error adding medication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Medication
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Medication</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="med-name" className="text-gray-900">Medication Name *</Label>
            <Input
              id="med-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Lisinopril"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="dosage" className="text-gray-900">Dosage *</Label>
            <Input
              id="dosage"
              value={formData.dosage}
              onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
              placeholder="e.g., 10mg"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="frequency" className="text-gray-900">Frequency *</Label>
            <select
              id="frequency"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
              required
            >
              <option value="">Select frequency...</option>
              <option value="Once daily">Once daily</option>
              <option value="Twice daily">Twice daily</option>
              <option value="Three times daily">Three times daily</option>
              <option value="Every 12 hours">Every 12 hours</option>
              <option value="As needed">As needed</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-date" className="text-gray-900">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="text-gray-900 border-2"
              />
            </div>
            <div>
              <Label htmlFor="end-date" className="text-gray-900">End Date (Optional)</Label>
              <Input
                id="end-date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="text-gray-900 border-2"
                min={formData.start_date}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="side-effects" className="text-gray-900">Side Effects</Label>
            <Textarea
              id="side-effects"
              value={formData.side_effects}
              onChange={(e) => setFormData({ ...formData, side_effects: e.target.value })}
              placeholder="List any known side effects..."
              className="text-gray-900 border-2"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="notes" className="text-gray-900">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              className="text-gray-900 border-2"
              rows={2}
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {loading ? 'Adding...' : 'Add Medication'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Log Medication Dialog
const LogMedicationDialog = ({ medicationId, medicationName }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scheduled_time: '',
    actual_time: '',
    status: 'taken',
    reason_if_missed: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        scheduled_time: new Date(formData.scheduled_time).toISOString(),
        actual_time: formData.actual_time ? new Date(formData.actual_time).toISOString() : new Date().toISOString()
      };

      const response = await fetch(`${API_URL}/api/medications/${medicationId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Medication logged successfully');
        setOpen(false);
        setFormData({
          scheduled_time: '',
          actual_time: '',
          status: 'taken',
          reason_if_missed: '',
          notes: ''
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to log medication');
      }
    } catch (error) {
      console.error('Error logging medication:', error);
      toast.error('Error logging medication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-2">
          <Activity className="w-4 h-4 mr-1" />
          Log
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log {medicationName}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="status" className="text-gray-900">Status *</Label>
            <select
              id="status"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              required
            >
              <option value="taken">Taken</option>
              <option value="missed">Missed</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>

          <div>
            <Label htmlFor="scheduled-time" className="text-gray-900">Scheduled Time *</Label>
            <Input
              id="scheduled-time"
              type="datetime-local"
              value={formData.scheduled_time}
              onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
              className="text-gray-900 border-2"
              required
            />
          </div>

          {formData.status === 'taken' && (
            <div>
              <Label htmlFor="actual-time" className="text-gray-900">Actual Time Taken</Label>
              <Input
                id="actual-time"
                type="datetime-local"
                value={formData.actual_time}
                onChange={(e) => setFormData({ ...formData, actual_time: e.target.value })}
                className="text-gray-900 border-2"
              />
              <p className="text-xs text-gray-600 mt-1">Leave blank to use current time</p>
            </div>
          )}

          {(formData.status === 'missed' || formData.status === 'skipped') && (
            <div>
              <Label htmlFor="reason" className="text-gray-900">Reason</Label>
              <Textarea
                id="reason"
                value={formData.reason_if_missed}
                onChange={(e) => setFormData({ ...formData, reason_if_missed: e.target.value })}
                placeholder="Why was this dose missed or skipped?"
                className="text-gray-900 border-2"
                rows={2}
              />
            </div>
          )}

          <div>
            <Label htmlFor="log-notes" className="text-gray-900">Notes</Label>
            <Textarea
              id="log-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              className="text-gray-900 border-2"
              rows={2}
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {loading ? 'Logging...' : 'Log Medication'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Provider Dialog
const AddProviderDialog = ({ providers, setProviders }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    specialty: '',
    practice: '',
    phone: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const newProvider = {
      id: Date.now(),
      ...formData
    };
    setProviders([...providers, newProvider]);
    toast.success('Provider added successfully');
    setOpen(false);
    setFormData({ name: '', specialty: '', practice: '', phone: '' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Provider
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Healthcare Provider</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="provider-name" className="text-gray-900">Provider Name *</Label>
            <Input
              id="provider-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Dr. Jane Smith"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="specialty" className="text-gray-900">Specialty *</Label>
            <select
              id="specialty"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.specialty}
              onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              required
            >
              <option value="">Select specialty...</option>
              <option value="Primary Care">Primary Care</option>
              <option value="Cardiologist">Cardiologist</option>
              <option value="Neurologist">Neurologist</option>
              <option value="Oncologist">Oncologist</option>
              <option value="Orthopedist">Orthopedist</option>
              <option value="Psychiatrist">Psychiatrist</option>
              <option value="Other Specialist">Other Specialist</option>
            </select>
          </div>

          <div>
            <Label htmlFor="practice" className="text-gray-900">Practice/Hospital</Label>
            <Input
              id="practice"
              value={formData.practice}
              onChange={(e) => setFormData({ ...formData, practice: e.target.value })}
              placeholder="City Medical Center"
              className="text-gray-900 border-2"
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-gray-900">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Add Provider
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Insurance Dialog
const AddInsuranceDialog = ({ policies, setPolicies }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    provider: '',
    policyNumber: '',
    type: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const newPolicy = {
      id: Date.now(),
      ...formData
    };
    setPolicies([...policies, newPolicy]);
    toast.success('Insurance policy added successfully');
    setOpen(false);
    setFormData({ provider: '', policyNumber: '', type: '' });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Policy
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Insurance Policy</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="provider" className="text-gray-900">Insurance Provider *</Label>
            <Input
              id="provider"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              placeholder="Blue Cross Blue Shield"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="policy-number" className="text-gray-900">Policy Number *</Label>
            <Input
              id="policy-number"
              value={formData.policyNumber}
              onChange={(e) => setFormData({ ...formData, policyNumber: e.target.value })}
              placeholder="ABC123456789"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="type" className="text-gray-900">Coverage Type *</Label>
            <select
              id="type"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
            >
              <option value="">Select type...</option>
              <option value="Primary Health Insurance">Primary Health Insurance</option>
              <option value="Medicare Part A">Medicare Part A</option>
              <option value="Medicare Part B">Medicare Part B</option>
              <option value="Medicare Part D">Medicare Part D</option>
              <option value="Supplemental/Medigap">Supplemental/Medigap</option>
              <option value="Dental">Dental</option>
              <option value="Vision">Vision</option>
            </select>
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Add Policy
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Care Log Dialog
const AddCareLogDialog = ({ onSuccess }) => {
  const { currentTeam } = useTeam();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    meals: '',
    mood_rating: 5,
    activities: '',
    sleep_quality: 5,
    bathroom_notes: '',
    mobility_notes: '',
    general_notes: '',
    vitals: {
      bp_systolic: '',
      bp_diastolic: '',
      weight: '',
      temp: '',
      pain: '',
      o2_sat: '',
      heart_rate: ''
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        // Clean up vitals - only send non-empty values
        vitals: Object.fromEntries(
          Object.entries(formData.vitals).filter(([_, v]) => v !== '')
        )
      };

      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/care-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Care log entry added successfully');
        setOpen(false);
        setFormData({
          date: new Date().toISOString().split('T')[0],
          meals: '',
          mood_rating: 5,
          activities: '',
          sleep_quality: 5,
          bathroom_notes: '',
          mobility_notes: '',
          general_notes: '',
          vitals: {
            bp_systolic: '',
            bp_diastolic: '',
            weight: '',
            temp: '',
            pain: '',
            o2_sat: '',
            heart_rate: ''
          }
        });
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add care log');
      }
    } catch (error) {
      console.error('Error adding care log:', error);
      toast.error('Error adding care log');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Log Entry
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Care Log Entry</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="log-date" className="text-gray-900">Date *</Label>
            <Input
              id="log-date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="meals" className="text-gray-900">Meals</Label>
            <Textarea
              id="meals"
              value={formData.meals}
              onChange={(e) => setFormData({ ...formData, meals: e.target.value })}
              placeholder="What did they eat today?"
              className="text-gray-900 border-2"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="mood" className="text-gray-900">Mood (1-10)</Label>
              <Input
                id="mood"
                type="number"
                min="1"
                max="10"
                value={formData.mood_rating}
                onChange={(e) => setFormData({ ...formData, mood_rating: parseInt(e.target.value) })}
                className="text-gray-900 border-2"
              />
            </div>
            <div>
              <Label htmlFor="sleep" className="text-gray-900">Sleep Quality (1-10)</Label>
              <Input
                id="sleep"
                type="number"
                min="1"
                max="10"
                value={formData.sleep_quality}
                onChange={(e) => setFormData({ ...formData, sleep_quality: parseInt(e.target.value) })}
                className="text-gray-900 border-2"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="activities" className="text-gray-900">Activities</Label>
            <Textarea
              id="activities"
              value={formData.activities}
              onChange={(e) => setFormData({ ...formData, activities: e.target.value })}
              placeholder="Activities and engagement today"
              className="text-gray-900 border-2"
              rows={2}
            />
          </div>

          <div>
            <Label className="text-gray-900 mb-2 block">Vital Signs (Optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="BP Systolic"
                type="number"
                value={formData.vitals.bp_systolic}
                onChange={(e) => setFormData({ ...formData, vitals: { ...formData.vitals, bp_systolic: e.target.value } })}
                className="text-gray-900 border-2"
              />
              <Input
                placeholder="BP Diastolic"
                type="number"
                value={formData.vitals.bp_diastolic}
                onChange={(e) => setFormData({ ...formData, vitals: { ...formData.vitals, bp_diastolic: e.target.value } })}
                className="text-gray-900 border-2"
              />
              <Input
                placeholder="Weight (lbs)"
                type="number"
                value={formData.vitals.weight}
                onChange={(e) => setFormData({ ...formData, vitals: { ...formData.vitals, weight: e.target.value } })}
                className="text-gray-900 border-2"
              />
              <Input
                placeholder="Temp (°F)"
                type="number"
                step="0.1"
                value={formData.vitals.temp}
                onChange={(e) => setFormData({ ...formData, vitals: { ...formData.vitals, temp: e.target.value } })}
                className="text-gray-900 border-2"
              />
              <Input
                placeholder="Heart Rate"
                type="number"
                value={formData.vitals.heart_rate}
                onChange={(e) => setFormData({ ...formData, vitals: { ...formData.vitals, heart_rate: e.target.value } })}
                className="text-gray-900 border-2"
              />
              <Input
                placeholder="O2 Sat (%)"
                type="number"
                value={formData.vitals.o2_sat}
                onChange={(e) => setFormData({ ...formData, vitals: { ...formData.vitals, o2_sat: e.target.value } })}
                className="text-gray-900 border-2"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="bathroom" className="text-gray-900">Bathroom Notes</Label>
            <Textarea
              id="bathroom"
              value={formData.bathroom_notes}
              onChange={(e) => setFormData({ ...formData, bathroom_notes: e.target.value })}
              placeholder="Bathroom activity notes"
              className="text-gray-900 border-2"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="mobility" className="text-gray-900">Mobility Notes</Label>
            <Textarea
              id="mobility"
              value={formData.mobility_notes}
              onChange={(e) => setFormData({ ...formData, mobility_notes: e.target.value })}
              placeholder="Movement and mobility notes"
              className="text-gray-900 border-2"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="general-notes" className="text-gray-900">General Notes</Label>
            <Textarea
              id="general-notes"
              value={formData.general_notes}
              onChange={(e) => setFormData({ ...formData, general_notes: e.target.value })}
              placeholder="Any other observations or notes"
              rows={3}
              className="text-gray-900 border-2"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {loading ? 'Adding...' : 'Add Log Entry'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Medical;
